// Wave 20.49cp — Aero-intensity heat map for the Discover screen.
//
// Renders the rider's (or the people they follow) session GPS
// points as a mapbox-gl heatmap layer, coloured by the same
// aero-intensity metric shown on session cards
// (avg_speed_mps + avg_headwind_mps).
//
// Backend: GET /heatmap?scope=me|following returns an aggregated
// grid of {lat, lng, intensity, count} cells. This component
// fetches, converts to a GeoJSON FeatureCollection, and mounts
// a mapbox-gl heatmap layer with intensity-scaled colour ramp.

import { fmtSpeed, useUnitSystem } from '../services/useUnitSystem';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import mapboxgl from 'mapbox-gl';
import { attachMinimalAttribution } from '../services/mapAttribution';
import 'mapbox-gl/dist/mapbox-gl.css';
import { colors } from '../theme';
import { useMapStyle, mapStyleUrl } from '../services/useMapStyle';

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';

interface HeatCell {
  lat: number;
  lng: number;
  intensity: number;
  count: number;
}

interface HeatResponse {
  cells: HeatCell[];
  meta: {
    min: number;
    max: number;
    points: number;
    cellCount: number;
    scope: 'me' | 'following';
    daysWindow: number;
  };
}

type Scope = 'me' | 'following';

export default function AeroHeatmap() {
  const units = useUnitSystem();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [scope, setScope] = useState<Scope>('me');
  const [data, setData] = useState<HeatResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mapStyle = useMapStyle();

  useEffect(() => {
    setLoading(true);
    setError(null);
    const jwt = localStorage.getItem(TOKEN_KEY);
    if (!jwt) {
      setError('Sign in to see your aero heat map.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    fetch(`${API}/heatmap?scope=${scope}`, {
      headers: { Authorization: `Bearer ${jwt}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((j: HeatResponse) => {
        if (cancelled) return;
        setData(j);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Heat map is unavailable — try again in a minute.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scope]);

  // Initial map setup.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const m = new mapboxgl.Map({
      container: containerRef.current,
      style: mapStyleUrl(mapStyle),
      center: [-71.06, 42.36],
      zoom: 9,
      accessToken: process.env.REACT_APP_MAPBOX_ACCESS_TOKEN,
      attributionControl: false,
      logoPosition: 'bottom-right',
    });
    attachMinimalAttribution(m);
    mapRef.current = m;
    return () => {
      m.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-apply style on rider preference change.
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    m.setStyle(mapStyleUrl(mapStyle));
  }, [mapStyle]);

  // Update the heat-map source + layer whenever data arrives.
  const geojson = useMemo(() => {
    if (!data) {
      return {
        type: 'FeatureCollection' as const,
        features: [] as any[],
      };
    }
    return {
      type: 'FeatureCollection' as const,
      features: data.cells.map((c) => ({
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [c.lng, c.lat] },
        properties: { intensity: c.intensity, count: c.count },
      })),
    };
  }, [data]);

  useEffect(() => {
    const m = mapRef.current;
    if (!m || !data) return;
    const setup = () => {
      if (m.getLayer('aero-heat')) m.removeLayer('aero-heat');
      if (m.getSource('aero-heat')) m.removeSource('aero-heat');
      m.addSource('aero-heat', {
        type: 'geojson',
        data: geojson as any,
      });
      const maxI = Math.max(1, data.meta.max);
      m.addLayer({
        id: 'aero-heat',
        type: 'heatmap',
        source: 'aero-heat',
        maxzoom: 15,
        paint: {
          'heatmap-weight': [
            'interpolate',
            ['linear'],
            ['get', 'intensity'],
            0, 0,
            maxI, 1,
          ],
          'heatmap-intensity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            0, 1,
            15, 3,
          ],
          'heatmap-color': [
            'interpolate',
            ['linear'],
            ['heatmap-density'],
            0, 'rgba(0, 0, 0, 0)',
            0.15, 'rgba(66, 133, 244, 0.65)',
            0.35, 'rgba(106, 255, 255, 0.75)',
            0.55, 'rgba(106, 255, 69, 0.80)',
            0.75, 'rgba(255, 209, 102, 0.85)',
            0.95, 'rgba(255, 90, 95, 0.95)',
          ],
          'heatmap-radius': [
            'interpolate',
            ['linear'],
            ['zoom'],
            5, 8,
            10, 18,
            15, 40,
          ],
          'heatmap-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            5, 0.9,
            15, 0.6,
          ],
        },
      });
      // Auto-fit to the cell set if there's data.
      if (data.cells.length > 0) {
        const b = new mapboxgl.LngLatBounds();
        for (const c of data.cells) b.extend([c.lng, c.lat]);
        try {
          m.fitBounds(b, { padding: 40, maxZoom: 11, animate: false });
        } catch {
          /* single-cell bounds throws — safe to ignore */
        }
      }
    };
    if (m.isStyleLoaded()) {
      setup();
    } else {
      m.once('style.load', setup);
    }
  }, [geojson, data]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Tabs
        value={scope}
        onChange={(_, v) => setScope(v as Scope)}
        sx={{
          minHeight: 32,
          '& .MuiTab-root': {
            minHeight: 32,
            fontSize: '0.75rem',
            fontWeight: 700,
            color: colors.textMuted,
            textTransform: 'none',
          },
          '& .Mui-selected': { color: `${colors.brand} !important` },
          '& .MuiTabs-indicator': { backgroundColor: colors.brand },
        }}
      >
        <Tab value="me" label="My rides" />
        <Tab value="following" label="People I follow" />
      </Tabs>

      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          height: 480,
          borderRadius: 2,
          border: `1px solid ${colors.border}`,
          overflow: 'hidden',
          position: 'relative',
        }}
      />

      {loading && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ color: colors.textMuted }}>
          <CircularProgress size={16} sx={{ color: colors.brand }} />
          <Typography sx={{ fontSize: '0.85rem' }}>Loading heat map…</Typography>
        </Stack>
      )}
      {error && (
        <Typography sx={{ color: colors.textMuted, fontSize: '0.85rem' }}>
          {error}
        </Typography>
      )}
      {!loading && !error && data && (
        <Typography sx={{ color: colors.textMuted, fontSize: '0.75rem' }}>
          {data.meta.cellCount} cells · {data.meta.points.toLocaleString()} points ·
          last {data.meta.daysWindow} days · intensity{' '}
          {fmtSpeed(data.meta.min, units)}–{fmtSpeed(data.meta.max, units)} (avg speed + headwind)
        </Typography>
      )}
    </Box>
  );
}
